import * as THREE from 'three';

export class PrayerFlagLine {
  public root: THREE.Group = new THREE.Group();
  private flagMeshes: THREE.Mesh[] = [];
  public rippleIntensity: number = 1.0;

  constructor(start: THREE.Vector3, end: THREE.Vector3, flagCount: number = 14) {
    this.root.name = 'PrayerFlagLine';

    // Wooden post 1 & 2
    const postGeo = new THREE.CylinderGeometry(0.12, 0.15, 3.2, 5);
    const postMat = new THREE.MeshStandardMaterial({ color: 0x5c4033, roughness: 0.9 });
    const post1 = new THREE.Mesh(postGeo, postMat);
    post1.position.copy(start);
    post1.position.y += 1.6;
    const post2 = new THREE.Mesh(postGeo, postMat);
    post2.position.copy(end);
    post2.position.y += 1.6;
    this.root.add(post1);
    this.root.add(post2);

    // Traditional 5 colors: Blue (sky), White (air/clouds), Red (fire), Green (water), Yellow (earth)
    const colors = [0x2563eb, 0xf8fafc, 0xdc2626, 0x16a34a, 0xeab308];

    // Hanging flags along catenary curve
    const flagGeo = new THREE.PlaneGeometry(0.5, 0.45, 2, 2);
    flagGeo.translate(0, -0.22, 0);

    for (let i = 0; i < flagCount; i++) {
      const alpha = (i + 1) / (flagCount + 1);
      const pos = new THREE.Vector3().lerpVectors(start, end, alpha);
      // Catenary sag
      pos.y += 3.0 - Math.sin(alpha * Math.PI) * 0.8;

      const mat = new THREE.MeshStandardMaterial({
        color: colors[i % colors.length],
        side: THREE.DoubleSide,
        roughness: 0.8,
        flatShading: true,
      });

      const flag = new THREE.Mesh(flagGeo, mat);
      flag.position.copy(pos);
      // Align along rope direction
      const dir = new THREE.Vector3().subVectors(end, start).normalize();
      flag.quaternion.setFromUnitVectors(new THREE.Vector3(1, 0, 0), dir);

      this.root.add(flag);
      this.flagMeshes.push(flag);
    }
  }

  public update(dt: number, time: number): void {
    if (this.rippleIntensity > 1.0) {
      this.rippleIntensity = Math.max(1.0, this.rippleIntensity - dt * 0.8);
    }

    for (let i = 0; i < this.flagMeshes.length; i++) {
      const flag = this.flagMeshes[i];
      const flutter = Math.sin(time * 6.0 + i * 0.8) * 0.3 * this.rippleIntensity;
      flag.rotation.z = flutter;
      flag.rotation.x = Math.cos(time * 5.0 + i) * 0.15 * this.rippleIntensity;
    }
  }

  public triggerRipple(): void {
    this.rippleIntensity = 3.5;
  }
}

export class CableCarSystem {
  public root: THREE.Group = new THREE.Group();
  private cabins: THREE.Group[] = [];
  private startPos: THREE.Vector3;
  private endPos: THREE.Vector3;
  private cabinSpeed: number = 0.04;
  private cabinAlphas: number[] = [0.15, 0.55, 0.85];

  constructor(start: THREE.Vector3, end: THREE.Vector3) {
    this.startPos = start;
    this.endPos = end;
    this.root.name = 'CableCarSystem';

    // Cable line
    const lineGeo = new THREE.BufferGeometry().setFromPoints([start, end]);
    const lineMat = new THREE.LineBasicMaterial({ color: 0x334155 });
    const cable = new THREE.Line(lineGeo, lineMat);
    this.root.add(cable);

    // Cable towers at 1/3 and 2/3
    this.createTower(new THREE.Vector3().lerpVectors(start, end, 0.35));
    this.createTower(new THREE.Vector3().lerpVectors(start, end, 0.70));

    // Cabins
    for (let i = 0; i < this.cabinAlphas.length; i++) {
      const cabin = this.createCabin(i % 2 === 0 ? 0xef4444 : 0xf59e0b);
      this.cabins.push(cabin);
      this.root.add(cabin);
    }
  }

  private createTower(pos: THREE.Vector3): void {
    const towerGeo = new THREE.CylinderGeometry(0.3, 0.6, 12, 4);
    const towerMat = new THREE.MeshStandardMaterial({ color: 0x64748b, metalness: 0.4 });
    const tower = new THREE.Mesh(towerGeo, towerMat);
    tower.position.copy(pos);
    tower.position.y += 6;
    this.root.add(tower);
  }

  private createCabin(colorHex: number): THREE.Group {
    const group = new THREE.Group();

    // Cabin box
    const bodyGeo = new THREE.BoxGeometry(1.2, 1.4, 1.2);
    const bodyMat = new THREE.MeshStandardMaterial({ color: colorHex, roughness: 0.4 });
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    body.position.y = -1.2;
    group.add(body);

    // Hanger arm
    const armGeo = new THREE.CylinderGeometry(0.04, 0.04, 1.2);
    const armMat = new THREE.MeshStandardMaterial({ color: 0x334155 });
    const arm = new THREE.Mesh(armGeo, armMat);
    arm.position.y = -0.5;
    group.add(arm);

    return group;
  }

  public update(dt: number): void {
    for (let i = 0; i < this.cabins.length; i++) {
      this.cabinAlphas[i] = (this.cabinAlphas[i] + this.cabinSpeed * dt) % 1.0;
      const alpha = this.cabinAlphas[i];
      const pos = new THREE.Vector3().lerpVectors(this.startPos, this.endPos, alpha);
      this.cabins[i].position.copy(pos);
    }
  }

  public getCabinPosition(index: number = 0): THREE.Vector3 {
    return this.cabins[index]?.position || this.startPos;
  }
}
